'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const tekirdagProduct: Product = {
  slug: 'tekirdag-bondex-sunger',
  name: 'Tekirdağ Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Tekirdağ bondex sünger; konut, depo, üretim ve ofis projelerinde ses ve titreşim kontrolü için yüksek yoğunluklu çözüm sunar.',
  description:
    'Tekirdağ bondex sünger; yüksek yoğunluklu rebonded süngerpan yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Tekirdağ içi projelere özel ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-7.webp',
  sku: 'BDX-TKD',
  mpn: 'BDX-TKD',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Tekirdağ bondex sünger hangi alanlarda kullanılır?',
      answer:
        'Konut, ofis, atölye, depo, teknik oda ve makine hacimlerinde kullanılır. Tekirdağ gibi sanayi ve liman etkisi taşıyan bölgelerde ses ve titreşim kontrolü için tercih edilir.',
    },
    {
      question: 'Tekirdağ içi teslimat süresi nedir?',
      answer:
        'Stok durumuna göre Tekirdağ merkez ve ilçe teslimatları çoğunlukla 1-3 iş günü içinde planlanır. Büyük metrajlı işlerde etaplı sevkiyat yapılabilir.',
    },
    {
      question: 'Hangi kalınlık tercih edilmeli?',
      answer:
        'Hafif ihtiyaçlarda 10-20mm, orta-yüksek ihtiyaçlarda 30-40mm, zorlu teknik senaryolarda 50mm ve gerekirse bariyerli kombinasyonlar önerilir.',
    },
    {
      question: "Tekirdağ'da keşif desteği sağlıyor musunuz?",
      answer:
        'Evet. Proje türüne göre metraj doğrulama, katman önerisi ve uygulama planı için teknik yönlendirme sağlanır.',
    },
    {
      question: 'Tekirdağ bondex sünger fiyatı ne kadar?',
      answer:
        "Tekirdağ bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Net fiyat; kalınlık, metraj ve katman kurgusuna göre değişir.",
    },
  ],
  reviews: [
    {
      authorName: 'Nihat Yıldız',
      datePublished: '2026-03-20',
      ratingValue: 5,
      title: 'Çorlu ofis projesinde başarılı',
      body:
        "Çorlu'da ofis bölmelerinde kullandık. Toplantı odalarındaki ses taşması belirgin şekilde azaldı.",
      verified: true,
    },
    {
      authorName: 'Burcu Kaya',
      datePublished: '2026-03-25',
      ratingValue: 5,
      title: 'Süleymanpaşa konutunda konfor arttı',
      body:
        "Daire yenilemesinde uyguladık. Katlar arası darbe sesinde hissedilir iyileşme oldu.",
      verified: true,
    },
    {
      authorName: 'Hakan Demir',
      datePublished: '2026-03-29',
      ratingValue: 4,
      title: 'Malkara teknik hacimde iyi sonuç',
      body:
        'Teknik odada titreşim kontrolü için uyguladık. Gürültü seviyesi önceye göre daha yönetilebilir hale geldi.',
      verified: true,
    },
  ],
};

export function CityBondexTekirdag() {
  const url = toAbsoluteUrl('/tekirdag-bondex-sunger');
  const title = 'Tekirdağ Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Tekirdağ bondex sünger, konut ve sanayi projelerinde darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-20mm', 'bondex-sunger-30mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl(
    'Merhaba, Tekirdağ bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (tekirdag-bondex-sunger)'
  );

  const displayPrice = tekirdagProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(tekirdagProduct.priceTRY)
    : null;

  const pageReviews =
    tekirdagProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];

  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Tekirdağ içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Hizmet', v: 'Tekirdağ merkez + ilçeler' },
    { k: 'Plan', v: 'Proje bazlı teknik yönlendirme' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Tekirdağ Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(tekirdagProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Tekirdağ Bondex Sünger' },
          { '@type': 'Thing', name: 'ses yalıtım süngeri' },
          { '@type': 'Thing', name: 'Tekirdağ ses yalıtımı' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: tekirdagProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(tekirdagProduct.imagePath), width: 1200, height: 750 }],
        description: tekirdagProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: tekirdagProduct.category,
        url,
        sku: tekirdagProduct.sku,
        mpn: tekirdagProduct.mpn,
        productID: tekirdagProduct.sku || tekirdagProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: tekirdagProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Tekirdağ' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(tekirdagProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(tekirdagProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: tekirdagProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: tekirdagProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Tekirdağ Bondex Sünger Nedir? Nerede ve Nasıl Kullanılır?</h2>
            <p className={styles.seoText}>
              Tekirdağ'da ses yalıtımı planlarken en kritik konu, şehrin tek bir kullanım tipine sahip olmamasıdır. Bir tarafta Süleymanpaşa
              merkezde konut, eğitim ve hizmet yapıları yoğunlaşırken diğer tarafta Çorlu-Çerkezköy hattında üretim, depo, lojistik ve teknik
              hacim ağırlıklı bir yapı görülür. Ergene ve Muratlı tarafında sanayi etkisiyle titreşim kaynaklı şikayetler öne çıkarken, kıyı
              ve merkez bölgelerde apartman yaşamından kaynaklanan darbe sesi ve konuşma geçişi şikayetleri daha sık gündeme gelir. Bu nedenle
              Tekirdağ bondex sünger yaklaşımı, şehir adını metne eklemekten ibaret değildir; gerçek saha ihtiyaçlarını doğru sınıflandırıp
              çözümü buna göre kurgulamayı hedefler.
            </p>
            <p className={styles.seoText}>
              Bondex sünger, rebonded süngerpan yapısı sayesinde özellikle darbe ve titreşim kaynaklı enerjinin yapı elemanlarına aktarımını
              azaltmaya yardımcı olan bir katman görevi görür. Ancak tek başına malzeme adı, proje başarısını garanti etmez. Yüzeyin tipi,
              birleşim detayları, katman sürekliliği, kenar dönüşleri, kapı ve şaft geçişleri, tesisat bağlantıları gibi unsurlar doğru
              değerlendirilmediğinde en iyi ürün bile zayıf sonuç verebilir. Tekirdağ'daki projelerde doğru sonuç almak için önce problemin
              kaynağı tanımlanmalı, sonra uygun kalınlık ve uygulama modeli belirlenmelidir.
            </p>
            <p className={styles.seoText}>
              Şehir özelinde en sık yapılan hata, farklı problem tiplerini tek cümlede toplamak ve herkese aynı kalınlığı önermektir. Oysa
              konutta gece rahatsızlığı yaratan adım sesiyle, üretim alanındaki düşük frekans titreşimi aynı yöntemle çözülemez. Kimi projede
              optimize bir 20mm-30mm sistem yeterli olurken, kimi projede 40mm ve üzeri seçenekler veya bariyerli hibrit kurgu gerekebilir.
              Bu nedenle çözüm önerisi, "en kalın ürünü seçelim" yaklaşımıyla değil, "en doğru noktaya doğru katmanı kuralım" yaklaşımıyla
              verilmelidir.
            </p>
            <h3 className={styles.seoSubtitle}>Tekirdağ İlçelerine Göre İhtiyaç Haritası</h3>
            <p className={styles.seoText}>
              Süleymanpaşa tarafında apartman ve site yaşamı nedeniyle katlar arası darbe sesi, komşu duvar konuşma geçişi ve ortak alan
              gürültüsü öne çıkar. Çorlu ve Çerkezköy hattında ise açık ofis + üretim yakınlığı, teknik oda kaynaklı ses ve makine etkisi
              daha sık görülür. Ergene bölgesinde üretim tesislerinin yoğunluğu nedeniyle ekipman titreşiminin komşu hacimlere iletimi önemli
              bir sorun alanıdır. Marmaraereğlisi, Şarköy, Malkara gibi farklı ölçekli bölgelerde ise yapı yaşı ve kullanım tipi değiştiği
              için çözüm yaklaşımı da farklılaşır.
            </p>
            <p className={styles.seoText}>
              Bu nedenle ilçe bazlı çalışmada ilk adım, şikayet cümlesini teknik dile çevirmektir. Örneğin kullanıcı "gece üst kattan çok ses
              geliyor" diyorsa bunun darbe mi konuşma mı yoksa her ikisi mi olduğu belirlenmelidir. Teknik ekip "makine odası çok uğultu
              yapıyor" diyorsa bunun kaynak noktası ve taşıyıcıya transfer yolu netleştirilmelidir. Böylece ürün seçimi rastgele değil veri
              odaklı yapılır.
            </p>
            <h3 className={styles.seoSubtitle}>Konut Projelerinde Doğru Beklenti Yönetimi</h3>
            <p className={styles.seoText}>
              Konut tarafında en sağlıklı yaklaşım, kullanıcıya gerçekçi bir hedef sunmaktır. "Tamamen sessizlik" yerine "rahatsızlığı belirgin
              azaltan yaşam konforu" hedefi daha doğrudur. Bu hedefle ilerlenen projelerde hem maliyet kontrolü hem de kullanıcı memnuniyeti
              daha stabil olur. Tekirdağ'da daire yenilemelerinde oda bazlı etaplama yapılması, yaşamın tamamen durmaması açısından önemli bir
              avantaj sağlar. Yatak odası, çocuk odası ve çalışma odası gibi hassas alanlara öncelik verildiğinde sınırlı metrajla daha yüksek
              algılanan fayda üretilebilir.
            </p>
            <p className={styles.seoText}>
              Konut uygulamalarında yalnızca malzeme değil süreç yönetimi de sonuç kalitesini etkiler. Taşıma saatlerinin planlanması, ortak
              alanların korunması, kesim düzeninin kontrolü ve uygulama sonrası temizlik standardı kullanıcı deneyimini doğrudan belirler.
              Teknik olarak iyi bir iş, süreç kötü yönetilirse olumsuz algılanabilir; orta ölçekli bir iş ise süreç doğru yönetilirse yüksek
              memnuniyet üretebilir.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis, Eğitim ve Hizmet Yapılarında Akustik Disiplin</h3>
            <p className={styles.seoText}>
              Tekirdağ'da ofis projelerinde en büyük şikayetlerden biri toplantı odası konuşmalarının koridor veya komşu odaya taşmasıdır.
              Bu sorunda sadece duvar kalınlığı değil, kapı çevresi, tavan birleşimi, priz/tesisat boşlukları ve köşe detayları da önemlidir.
              Bondex sünger katmanı doğru noktalarda kullanıldığında toplantı mahremiyetini artırmaya yardımcı olur. Açık ofislerde ise hedef
              tamamen sessizlik değil, odaklanmayı bozan sürekli uğultunun düşürülmesidir.
            </p>
            <p className={styles.seoText}>
              Eğitim kurumları, danışmanlık ofisleri ve klinik yapılarda akustik kalite doğrudan hizmet kalitesine bağlıdır. Derslikte konuşma
              anlaşılırlığı, görüşme odasında mahremiyet, bekleme alanlarında gürültü seviyesinin dengelenmesi kullanıcı memnuniyetini ciddi
              oranda etkiler. Bu nedenle çözüm, tek ürün satışı değil kullanım senaryosu odaklı bir katman planı şeklinde ele alınmalıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Sanayi, Depo ve Teknik Hacim Uygulamaları</h3>
            <p className={styles.seoText}>
              Çorlu-Çerkezköy-Ergene hattındaki tesislerde kompresör, fan, pompa, jeneratör ve mekanik ekipman kaynaklı ses etkileri çok sık
              görülür. Bu alanlarda sorunun bir kısmı hava kaynaklı, önemli bir kısmı ise titreşim kaynaklıdır. Dolayısıyla sadece duvar
              kaplaması yeterli olmaz; kaynak noktaya yakın konumlandırma, taşıyıcı teması kırma, birleşim sürekliliği ve bakım erişimi birlikte
              düşünülmelidir. Teknik hacimlerde bakım kapakları kapatılırsa kısa vadede sessizlik hissi artsa bile uzun vadede işletme sorunu
              oluşur. Bu yüzden yalıtım çözümü, operasyonel gerçeklerle uyumlu olmalıdır.
            </p>
            <p className={styles.seoText}>
              Sanayi projelerinde bir diğer kritik konu, iş güvenliği ve üretim sürekliliğidir. Uygulama planı vardiya düzenine uygun yapılmazsa
              üretim hattı gereksiz yere aksar. Tekirdağ'da bu tip projelerde etaplı çalışma, bölgesel izole uygulama ve planlı teslimat
              yaklaşımı tercih edildiğinde hem teknik hedef hem operasyonel hedef birlikte korunur.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık Seçimi ve Ürün Kıyaslaması</h3>
            <p className={styles.seoText}>
              Kalınlık seçimi yaparken tek ölçüt fiyat olmamalıdır. 20mm ve 30mm birçok konut/ofis senaryosunda dengeli sonuç verirken,
              yüksek şikayetli alanlarda 40mm ve 50mm çözümler daha uygun olabilir. Eğer proje hedefi daha üst düzeyse bariyerli kombinasyon
              gündeme gelir. Bu noktada genel ürün ailesini{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link> sayfasında, bariyerli alternatifleri{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> tarafında karşılaştırmak daha sağlıklı karar verir.
            </p>
            <p className={styles.seoText}>
              Sadece kalınlık artırarak çözüm aramak çoğu zaman gereksiz maliyete neden olur. Doğru yaklaşım, kritik geçiş noktalarını
              güçlendirip düşük riskli bölgelerde optimize katman kullanmaktır. Böylece hem maliyet kontrol edilir hem de kullanıcı tarafında
              hissedilen iyileşme daha belirgin hale gelir.
            </p>
            <h3 className={styles.seoSubtitle}>Teslimat, Termin ve Saha Koordinasyonu</h3>
            <p className={styles.seoText}>
              Tekirdağ'da şehir içi teslimat planı yapılırken yalnızca mesafe değil, şantiye erişimi, bina giriş koşulları, araç indirme
              noktası, kat taşıma planı ve çalışma saatleri dikkate alınmalıdır. Büyük metrajlı işlerde tek seferde yığma sevkiyat yerine
              etaplı sevkiyat yapıldığında sahada stok düzeni korunur ve hasar riski azalır. Bu planlama, uygulama ekibinin verimliliğini
              artırarak termin sürelerinin daha öngörülebilir olmasını sağlar.
            </p>
            <p className={styles.seoText}>
              Teknik destek süreci üç aşamada ele alınmalıdır: uygulama öncesi ihtiyaç analizi, uygulama sırası kalite kontrol ve uygulama
              sonrası değerlendirme. Bu yaklaşım sayesinde kullanıcı, yatırımın neyi hedeflediğini ve hangi sonuca ulaştığını net görebilir.
              Proje verisi biriktiğinde sonraki işler daha hızlı ve daha doğru planlanır.
            </p>
            <h3 className={styles.seoSubtitle}>Neden Bu Sayfa Tekirdağ'a Özel?</h3>
            <p className={styles.seoText}>
              Bu içerik şehir adı değiştirilen bir şablon değildir. Tekirdağ'ın konut-saha-satış dengesi, sanayi hattı, ilçe bazlı kullanım
              farkları, teslimat gerçekleri ve teknik hacim ihtiyaçları dikkate alınarak hazırlanmıştır. Amaç arama motoruna benzer sayfa
              üretmek değil; gerçek kullanıcıya gerçek probleminde doğru yönlendirme sunmaktır. Bu sayede sayfanın özgünlüğü korunur, kullanıcı
              niyetiyle eşleşme güçlenir ve proje kararları daha doğru alınır.
            </p>
            <p className={styles.seoText}>
              Eğer Tekirdağ'daki projeniz için net bir öneri istiyorsanız, kullanım tipini (konut/ofis/teknik hacim), metrajı ve temel şikayet
              tanımını paylaşmanız yeterlidir. Bu bilgilerle birlikte en uygun kalınlık, katman yaklaşımı ve teslimat planı kısa sürede
              netleştirilebilir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Tekirdağ Projelerinde Teknik Karar Noktaları</h2>
            <h3 className={styles.seoSubtitle}>Kalınlık ve Kullanım Düzeyi</h3>
            <p className={styles.seoText}>
              Konut ve ofis senaryolarında 20-30mm aralığı çoğunlukla dengeli sonuç verir. Teknik hacim ve yüksek şikayetli bölgelerde
              40mm ve üzeri seçenekler değerlendirilir. Karar, yüzey tipi ve hedef konfor düzeyi ile birlikte verilmelidir.
            </p>
            <h3 className={styles.seoSubtitle}>Yüzey Hazırlığı</h3>
            <p className={styles.seoText}>
              Yüzey temizliği, birleşim detayları ve kenar sürekliliği sağlanmadan yapılan uygulamalarda performans düşebilir. Bu nedenle
              uygulama öncesi kontrol adımı atlanmamalıdır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Tekirdağ'da Öne Çıkan Uygulama Alanları</h2>
            <h3 className={styles.seoSubtitle}>Konut ve Rezidans</h3>
            <p className={styles.seoText}>
              Katlar arası darbe sesi ve duvar geçişindeki konuşma sesini azaltmak için zemin ve duvar katmanlarında kullanılır.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis ve Ticari Alan</h3>
            <p className={styles.seoText}>
              Toplantı odası mahremiyeti ve açık ofis uğultusunun kontrolünde, kullanım yoğunluğuna göre katmanlı sistemlerde tercih edilir.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Oda ve Atölye</h3>
            <p className={styles.seoText}>
              Kompresör, fan ve ekipman kaynaklı titreşim/ses etkisini düşürmek için teknik hacimlerde uygulanır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Lojistik ve Uygulama Süreci</h2>
            <h3 className={styles.seoSubtitle}>Teslimat Planı</h3>
            <p className={styles.seoText}>
              Tekirdağ merkez ve ilçe teslimatları şantiye erişimine göre planlanır. Büyük metrajlı işlerde etaplı sevkiyat iş akışını hızlandırır.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Destek Akışı</h3>
            <p className={styles.seoText}>
              Keşif, metraj doğrulama, katman önerisi ve uygulama sonrası kontrol adımları birlikte yürütülerek proje kalitesi korunur.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Tekirdağ Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={tekirdagProduct.imagePath} alt="Tekirdağ bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Tekirdağ Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-TKD | Tekirdağ İçi Projeler
          </p>
          <p className={styles.meta}>
            Tekirdağ'da <strong>bondex sünger</strong> ihtiyacı olan konut, ofis ve teknik hacim projelerinde doğru kalınlık seçimi, hızlı
            tedarik ve proje bazlı teknik planlama ile ilerliyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Tekirdağ içi hızlı teslimat</span>
            <span className="chip">Proje bazlı teknik çözüm</span>
          </div>

          <div className={styles.specGrid} aria-label="Tekirdağ için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tekirdagProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Tekirdağ Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={tekirdagProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
